import { FormControlLabel, FormLabel, Radio, RadioGroup, Stack } from '@mui/material';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { pushNotificationHandler } from '../../../common/PushNotification';
import RestHttpClient from '../../../common/RestHttpClient';
import {
  GetDetailResponse,
  LeasmanDissolveValue,
  PurchaseBy,
  SaveDashboardConfigurationRequest,
  SaveDissolveValuesRequest,
  SendPurchaseApplicationRequest,
} from '../../../generated/ApiClient';
import { PurchaseApplicationDialog } from '../applications/PurchaseApplicationDialog';
import { OverlayDissolveDetailEmpty } from './OverlayDissolveDetailEmpty';
import { OverlayDissolveDetailResidual } from './OverlayDissolveDetailResidual';
import { OverlayDissolveDetailValue } from './OverlayDissolveDetailValue';
import { DissolveValueEntry, DissolveValueSelection } from './OverlayDissolveValues';
import { isSameDay } from './OverlayDissolveValues.utils';

export interface DissolveValueDetail extends DissolveValueEntry {
  addition?: number;
  deduction?: number;
  commisionNumber?: string;
  purchasePrice?: number;
  isSurchargeCancelled?: boolean;
}

export function OverlayDissolveDetail({
  selected,
  dissolveEntries,
  cmsId,
  surcharge,
  pricesIncludingVAT,
  vatCoefficient,
  showPurchaseByOption,
  contractEndDate,
  detailData,
}: {
  selected: DissolveValueSelection;
  dissolveEntries: DissolveValueEntry[];
  cmsId: string;
  surcharge: number;
  pricesIncludingVAT: boolean;
  vatCoefficient: number;
  showPurchaseByOption: boolean;
  contractEndDate?: Date;
  detailData: GetDetailResponse;
}) {
  const dissolveDates = dissolveEntries
    .map((dissolveValue) => dissolveValue.date)
    .filter((x) => x !== undefined) as Date[];

  const config = detailData.configuration;

  const { data: existingDetails = [] } = useQuery({
    queryKey: ['dissolve', cmsId],
    queryFn: () => RestHttpClient.getDissolveValues(Number(cmsId), dissolveDates),
    select: (x) => x.data,
  });

  const {
    data: applicationResponse,
    isLoading: isApplicationLoading,
    mutate: sendApplicationRequest,
  } = useMutation({
    mutationFn: (purchasePrice: number) => {
      const noMboeCharge = removeSurcharge || (selectedDetail?.isSurchargeCancelled ?? false);
      return RestHttpClient.sendPurchaseApplication(
        new SendPurchaseApplicationRequest({
          contractNumber: cmsId,
          dealId: dealId!,
          date: selectedDetail?.date ?? contractEndDate ?? minDate,
          noMboeCharge: noMboeCharge,
          purchasePrice: purchasePrice,
          isFollowUp: !!selectedDetail?.commisionNumber,
          commissionNumber: selectedDetail?.commisionNumber,
          isPurchasesByLesee: purchaseByLessee,
        })
      );
    },
  });

  const [isRequestDialogOpen, setIsRequestDialogOpen] = useState(false);

  const residualSubsidy = detailData.contract.residualValueSubsidy ?? 0;

  const dealId = detailData.dealId;
  const purchaseByOptions: PurchaseBy[] = [];

  const financingTypeId = detailData?.contract.financingTypeId;
  const nonLesseeFinancingTypes = ['OL', 'CW', 'FS'];

  if (financingTypeId !== 'MI') {
    purchaseByOptions.push(PurchaseBy.Dealer);
  }

  if (!nonLesseeFinancingTypes.includes(financingTypeId)) {
    purchaseByOptions.push(PurchaseBy.Lessee);
  }

  const initialPurchaseBy =
    config?.purchaseBy ?? (showPurchaseByOption ? purchaseByOptions[0] : undefined);
  const [purchaseBy, setPurchaseBy] = useState(initialPurchaseBy);
  const [dissolveDetails, setDissolveDetails] = useState<DissolveValueDetail[]>([]);

  const residualLabel = t('dashboard.contractsOverlay.dissolveValues.residualValue');
  const minDate = new Date(1901, 11, 13, 12, 0, 0); // min date in js, set to 12:00 PM

  useEffect(() => {
    setDissolveDetails(
      dissolveEntries.map((entry) => {
        const existingDetail = existingDetails.find(
          (x) =>
            (isSameDay(x.dissolveDate, entry.date) &&
              x.dissolveValue.toFixed(2) === entry.value.toFixed(2)) ||
            (isSameDay(x.dissolveDate, minDate) && entry.label === residualLabel)
        );

        return {
          ...entry,
          addition: existingDetail?.addition,
          deduction: existingDetail?.deduction,
          commisionNumber: existingDetail?.commissionNumber,
          purchasePrice: existingDetail?.purchasePrice,
          isSurchargeCancelled: existingDetail?.isSurchargeCancelled,
        };
      })
    );
  }, [cmsId]);

  const selectedDetail = dissolveDetails.find((x) => x.type === selected);

  const purchaseByLessee = showPurchaseByOption && purchaseBy === PurchaseBy.Lessee;
  const purchaseByDealer = showPurchaseByOption && purchaseBy === PurchaseBy.Dealer;

  const removeChanges = purchaseByLessee;
  const removeSurcharge = purchaseByDealer;

  const saveDissolveValues = async () => {
    if (dealId && config?.rate) {
      const configuration = new SaveDashboardConfigurationRequest({
        cmsId: cmsId,
        dealId: dealId,
        displayAsGross: pricesIncludingVAT,
        rate: config?.rate,
        purchaseBy: purchaseBy,
      });
      await RestHttpClient.saveDashboardConfiguration(configuration);
    }

    const req = new SaveDissolveValuesRequest({
      cmsId: Number(cmsId),
      dissolveValues: dissolveDetails.map((x) => {
        return new LeasmanDissolveValue({
          commissionNumber: x.commisionNumber,
          cmsId: Number(cmsId),
          dissolveDate: x.date ?? minDate,
          dissolveValue: x.value,
          addition: removeChanges ? undefined : x.addition,
          deduction: removeChanges ? undefined : x.deduction,
          purchasePrice: removeChanges ? undefined : x.purchasePrice,
          isSurchargeCancelled: x.isSurchargeCancelled ?? false,
        });
      }),
    });

    await RestHttpClient.saveDissolveValues(req);
    pushNotificationHandler.publish(
      t('dashboard.contractsOverlay.dissolveValues.saved'),
      'success'
    );
  };

  const setSelectedDetail = (detail: DissolveValueDetail) => {
    setDissolveDetails((prevDetails) => prevDetails.map((d) => (d.type === selected ? detail : d)));
  };

  const requestApplication = async (purchasePrice: number) => {
    await saveDissolveValues();
    setIsRequestDialogOpen(true);
    await sendApplicationRequest(purchasePrice);
  };

  if (!selectedDetail || !dealId) {
    return null;
  }

  return (
    <Stack gap={2} key={cmsId}>
      {showPurchaseByOption && (
        <Stack>
          <FormLabel>{t('dashboard.contractsOverlay.dissolveValues.purchaseBy')}</FormLabel>
          <RadioGroup
            value={purchaseBy}
            onChange={(_event, value) => setPurchaseBy(value as PurchaseBy)}
            sx={{ flexDirection: 'row' }}
          >
            {purchaseByOptions.map((x) => (
              <FormControlLabel
                key={x}
                control={<Radio />}
                value={x}
                label={t(`dashboard.contractsOverlay.dissolveValues.${x}`)}
              />
            ))}
          </RadioGroup>
        </Stack>
      )}

      {removeChanges ? (
        <OverlayDissolveDetailEmpty
          onSave={saveDissolveValues}
          requestApplication={() => requestApplication(selectedDetail.value)}
        />
      ) : selectedDetail.type === DissolveValueSelection.Residual ? (
        <OverlayDissolveDetailResidual
          selectedDetail={selectedDetail}
          setSelectedDetail={setSelectedDetail}
          surcharge={surcharge}
          vatCoefficient={vatCoefficient}
          onSave={saveDissolveValues}
          removeSurcharge={removeSurcharge}
          requestApplication={requestApplication}
          residualSubsidy={residualSubsidy}
        />
      ) : (
        <OverlayDissolveDetailValue
          selectedDetail={selectedDetail}
          setSelectedDetail={setSelectedDetail}
          surcharge={surcharge}
          pricesIncludingVAT={pricesIncludingVAT}
          vatCoefficient={vatCoefficient}
          onSave={saveDissolveValues}
          onlyPurchase={removeSurcharge}
          requestApplication={requestApplication}
          residualSubsidy={residualSubsidy}
        />
      )}
      <PurchaseApplicationDialog
        isOpen={isRequestDialogOpen}
        setIsOpen={setIsRequestDialogOpen}
        contractNumber={cmsId}
        dealId={dealId}
        isApplicationLoading={isApplicationLoading}
        fileId={applicationResponse?.data.fileId}
      />
    </Stack>
  );
}
